import { graphql, Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import React from "react";
import Grid, { Col } from "../../components/grid/Grid";
import Meta from "../../components/meta/Meta";
import Quote from "../../components/quote/Quote";
import Section from "../../components/section/Section";
import Video from "../../components/video/Video";
import ShowcaseHeader from "../../components/showcaseHeader/showcaseHeader";
import * as styles from "./hsbc-klir.module.css";

const Klir = ({ data }) => {
  return (
    <article>
      <ShowcaseHeader
        title={data.content.frontmatter.title}
        tagline={data.content.frontmatter.tagline}
        largeTagline
        backgroundImage={data.content.hero && data.content.hero.childImageSharp}
        backgroundAlt=""
        widerStrapline
      />

      <Section padding="top">
        <Grid padding="bottom">
          <Col start={3} span={8}>
            <h2 className="h2Bigger">Small and medium-sized businesses (SMBs) are the backbone of the UK economy, yet they are often underserved and misunderstood by the banking industry.</h2>
            <p>HSBC saw an opportunity to change that. Their vision was to create an AI-driven app designed to help business owners manage their finances more effectively and ultimately succeed.</p>
            <p>We brought that vision to life by creating and launching <span className="fontWeightBold">Klir</span><span className="asterisk">*</span>, a financial tool that uses machine learning to forecast transactions and provide business owners with clearer financial insights.</p>
            <p className="smallBodyText"><span className="asterisk">*</span> The learnings and technology from Klir were later incorporated into Kinetic, HSBC’s award-winning mobile business banking app.</p>
            <p>But first, let’s rewind and share the process of building Klir.</p>
          </Col>
        </Grid>
      </Section>

      <Video
        src="https://assets.ustwo.com/video/klir-video-01.mp4"
        staticPoster={<StaticImage src="https://assets.ustwo.com/video/klir-video-poster.png" alt="Watch our video about how we made HSBC's Klir" />}
        description="A video about how we made HSBC's Klir"
        label="Play"
        lowerPlayButton
      />

      <Section padding="both">
        <Grid verticalCenter padding="bottom">
          <Col start={3} span={5}>
            <div>
              <h2>From concept to market</h2>
              <p>Over 22 months, we took Klir from alpha to beta, and into the hands of real users. We:</p>
              <ul className="bulletList">
                <li>Led all design and research work, including UX/UI, and defined the brand’s visuals, voice, and values.</li>
                <li>Developed the full-stack front and back end, integrating machine learning-driven insights and recommendations for iOS and Android.</li>
              </ul>
            </div>
          </Col>
          <Col start={8} span={4}>
            <div className={styles.phoneFrameWrapper}>
              <Video src="https://assets.ustwo.com/video/klir-phone-video-01.mp4" className={styles.phoneFrameVideo} autoPlay noPauseButton />
              <StaticImage src="https://assets.ustwo.com/images/work/klir-phone-frame-02.png" className="" alt="" />
            </div>
          </Col>
        </Grid>

        <Grid orderFix verticalCenter padding="bottom">
          <Col start={2} span={4}>
            <StaticImage src="https://assets.ustwo.com/images/work/klir-phone-02.png" className="mobileImageFullScreen" alt="" />
            {/* <div className={styles.phoneFrameWrapper}>
              <Video src="https://assets.ustwo.com/video/klir-phone-video-02.mp4" className={styles.phoneFrameVideo} autoPlay noPauseButton />
              <StaticImage src="https://assets.ustwo.com/images/work/klir-phone-frame-01.png" className="" alt="" />
            </div> */}
          </Col>
          <Col start={6} span={5}>
            <div>
              <ul className="bulletList">
                <li>Defined the business value of the machine learning model, ensuring it worked for both HSBC and business owners.</li>
                <li>Launched and ran a beta phase, gathering insights from 32 users to continuously refine the product.</li>
              </ul>
            </div>
          </Col>
        </Grid>

        <Grid padding="top">
          <Col start={3} span={8}>
            <h2>A people-first product</h2>
            <p>Klir was designed for business owners at a critical stage in their journey – typically two years in, when they need solutions to better manage their finances, and time becomes their most valuable resource.</p>
            <p>But not all businesses are the same. A retail shop handling daily transactions has very different financial needs from a consultant working on long-term contracts. We built Klir to adapt to different business models, ensuring that every business owner could get the right financial insights for them.</p>
            <p>We conducted research continuously over a two-year period; from initial interviews with business owners to ongoing discovery throughout the beta phase. What we found was clear: small businesses need financial tools that are easy to use, time-saving, and built for everyday people – not just qualified accountants.</p>
            <p>That’s why we designed Klir to feel more like a consumer app than a corporate banking tool, using friendly visuals, approachable language, and a clear user experience.</p>
          </Col>
        </Grid>

      </Section>

      <StaticImage src="https://assets.ustwo.com/images/work/klir-hsbc-ustwo-01.jpg" alt="" />

      <Section padding="both">
        <Grid padding="bottom">
          <Col start={3} span={8}>
            <h2>AI: Information for impact</h2>
            <p>Klir combined financial data from users’ banking and accounting software, and integrated it through machine learning to deliver faster, clearer financial insights.</p>
            <p>It was a groundbreaking example of how AI can help small businesses manage their finances more efficiently and holistically by:</p>
            <ul>
              <li>Tracking cash flow and forecasting expenses, helping businesses plan ahead.</li>
              <li>Highlighting late payments and making it easier to send email reminders straight from the app.</li>
              <li>Providing real-time notifications when payments arrive, and advising on how much should be allocated for taxes.</li>
              <li>Answering important financial questions via chatbot, like <span className="fontItalic">“What is my profit margin this month?”</span></li>
            </ul>
          </Col>
        </Grid>
        <StaticImage src="https://assets.ustwo.com/images/work/klir-hsbc-ustwo-03.jpg" alt="" />
        <Grid padding="top">
          <Col start={3} span={8}>
            <h2>A win-win for UK businesses and HSBC</h2>
            <p>For SMBs, Klir automated what would otherwise be a time-consuming process of sorting through spreadsheets just to understand their financial position. It allowed business owners to focus on running their business – or maybe even take a well-earned day off.</p>
            <p>For HSBC, Klir’s aggregated financial data provided a deeper understanding of SMBs – a customer base that banks have historically struggled to serve effectively. These insights helped HSBC iterate and create better banking solutions for businesses.</p>
            <p>Klir’s development followed the rise of open banking in the UK, as financial service regulations granted users more control over their financial data. Previously, business owners could only access their banking information through bank user interfaces.</p>
            <p>Open banking APIs allowed products like Klir to securely integrate multiple financial sources, giving business owners a more holistic financial picture.</p>
          </Col>
        </Grid>
      </Section>

      <Section padding="both"lightOnDark className={styles.altBackground}>
        <Grid>
          <Col start={3} span={8}>
            <h2>A startup mindset inside a global bank</h2>
            <p>Working on Klir felt like building a fintech startup within a large banking institution. We were driven by an incubator spirit, bringing together product, design, and tech experts, while working closely with accountants, finance specialists, and HSBC stakeholders.</p>
            <p>It was a chance to flex our process muscles – balancing the dynamics of building a breakthrough product that was not only innovative, but also secure and compliant with strict UK financial regulations.</p>
            <p>As with all projects, we worked with HSBC as one team. For us, success starts when our clients take over and make the product their own. To ensure a smooth transition, we supported HSBC in recruiting the best product leads, engineers, and designers to continue the work after we stepped away. </p>
          </Col>
        </Grid>
      </Section>

      <Section padding="both">
        <Grid>
          <Col start={3} span={8}>
            <h2>Time is money</h2>
            <p>Klir makes it easier for business owners to succeed financially, and gives them something even more valuable in return: time to pursue whatever inspired them to start a business in the first place.</p>
            <p className="gridSpacingBottom">By simplifying financial decision-making, Klir helps businesses make smarter choices, faster. Instead of second-guessing their numbers, they get clear, actionable insights, so they can stay financially afloat and make sound business decisions with confidence.</p>
            <Quote
              content={data.quote01.frontmatter.quote}
              author={data.quote01.frontmatter.author}
              authorTitle={data.quote01.frontmatter.authorTitle}
              largerText
              avatar={<StaticImage src="https://assets.ustwo.com/images/people/avatar-greig-robinson.jpg" alt="Photo of Greig Robinson" />}
            />
          </Col>
        </Grid>
      </Section>

      <StaticImage src="https://assets.ustwo.com/images/work/klir-footer.png" alt="" />

    </article>
  );
};

export default Klir;

export const Head = ({ data }) => {
  return (
    <Meta
      title={`${data.content.frontmatter.title} x ustwo`}
      description={data.content.frontmatter.description}
      imgsrc={data.content.thumbnail && data.content.thumbnail.publicURL}
    />
  );
}

export const query = graphql`
  query {
    content: mdx(frontmatter: { name: { eq: "klir" } }) {
      ...PageDefault
      images {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
    quote01: markdownRemark(frontmatter: { name: { eq: "klir-quote01" } }) {
      frontmatter {
        quote
        author
        authorTitle
      }
    }
  }
`;